/**
 * Objects API transforms
 *
 * Pure helpers that normalize backend object payloads into the frontend
 * shape and massage string-list inputs. `transformObject` is load-bearing.
 * Contact reads are canonical (#3756 Step 6): the input `??` chains read only
 * canonical attribute names (the backfilled store + chokepoint guarantee them);
 * the legacy divergent-name fallbacks (the org / x-handle aliases on raw
 * attributes) were dropped. Two transitional reads remain by design until FE
 * query caches expire: the singular email/phone passed to `buildContactElements`
 * (folds a cached pre-backfill singular value), and the org / x-handle top-level
 * OUTPUT aliases still emitted for downstream readers not yet on canonical names.
 */

import { CONVERSATION_SCOPE_IDS_KEY } from './objects.scope-keys'

export function extractStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
  }
  if (value === null || value === undefined) return []
  const parsed = String(value).trim()
  return parsed ? [parsed] : []
}

export function dedupeStringList(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)))
}

/** Which source supplied a contact element value (multi-provider contacts §3.5). */
export interface ElementSource {
  providerIntegrationId: string
  sourceType: string
}

/**
 * Single-valued conflict tracking for a scalar contact field (§3.6). When two
 * provider accounts vouch for divergent values of the same scalar field (e.g.
 * `jobTitle`), the backend writer (`packages/persistence/.../contact-sync.ts`)
 * raises `conflicted` and records each observed value with the source(s) that
 * vouch for it — never silently last-writer-wins. The read-side surfaces this as
 * a conflict indicator; native/legacy contacts carry none.
 */
export interface FieldConflict {
  conflicted: boolean
  candidates: Array<{ value: unknown; sources?: ElementSource[] }>
}

/**
 * The normalized shape of a single contact email/phone — the SINGLE source of
 * truth carrying both the value AND its per-source provenance. The UI renders
 * `emailEntries`/`phoneEntries` (value + provider icons together); the plain
 * `emails`/`phones` string arrays are derived from these (`.map(e => e.value)`)
 * for the many legacy readers. This is the unification that retired the old
 * string-vs-`{value,sources}` dual shape that crashed the grid (§3.5).
 */
export interface ContactElement {
  value: string
  primary?: boolean
  sources?: ElementSource[]
}

function elementValue(raw: unknown): string {
  if (typeof raw === 'string') return raw
  if (raw && typeof raw === 'object' && typeof (raw as { value?: unknown }).value === 'string') {
    return (raw as { value: string }).value
  }
  return ''
}

function unionElementSources(a: ElementSource[] | undefined, b: ElementSource[] | undefined): ElementSource[] {
  const out = [...(a ?? [])]
  for (const s of b ?? []) {
    if (s?.providerIntegrationId && !out.some((x) => x.providerIntegrationId === s.providerIntegrationId && x.sourceType === s.sourceType)) {
      out.push(s)
    }
  }
  return out
}

/**
 * Normalize a contact's emails/phones — a mix of plain strings (native/legacy)
 * and `{ value, primary, sources }` objects (multi-provider sync), plus the
 * singular `email`/`phone` field — into ONE deduped rich list. Dedupes by
 * lowercased value, unions provenance `sources`, and preserves any `primary`
 * flag. Empty/invalid entries are dropped.
 */
export function buildContactElements(arrayField: unknown, singleField?: unknown): ContactElement[] {
  const byKey = new Map<string, ContactElement>()
  const add = (raw: unknown) => {
    const value = elementValue(raw).trim()
    if (!value) return
    const key = value.toLowerCase()
    const sources = raw && typeof raw === 'object' && Array.isArray((raw as { sources?: unknown }).sources)
      ? ((raw as { sources: ElementSource[] }).sources)
      : []
    const primary = raw && typeof raw === 'object' ? Boolean((raw as { primary?: unknown }).primary) : false
    const prev = byKey.get(key)
    if (prev) {
      if (sources.length) prev.sources = unionElementSources(prev.sources, sources)
      if (primary) prev.primary = true
    } else {
      const entry: ContactElement = { value }
      if (primary) entry.primary = true
      if (sources.length) entry.sources = sources
      byKey.set(key, entry)
    }
  }
  ;(Array.isArray(arrayField) ? arrayField : []).forEach(add)
  add(singleField)
  return [...byKey.values()]
}

/**
 * Transform backend object format to frontend format
 */
export function transformObject(obj: any, conversationId?: string, etag?: string): any {
  const resolvedConversationIds =
    obj.data?.[CONVERSATION_SCOPE_IDS_KEY] ||
    obj.attributes?.[CONVERSATION_SCOPE_IDS_KEY] ||
    obj[CONVERSATION_SCOPE_IDS_KEY] ||
    (conversationId ? [conversationId] : [])

  const baseTransform = {
    ...obj, // Spread original object first
    // Then override with normalized fields (these take precedence)
    id: obj.id,
    type: obj.object_type || obj.type,
    what: obj.name || obj.title, // For tasks
    title: obj.title || obj.name,
    name: obj.name || obj.title, // Ensure name is always set from title if not present
    completed: obj.status === 'done' || obj.data?.status === 'done' || obj.attributes?.status === 'done',
    completed_at: obj.completedAt || obj.data?.completed_at || obj.attributes?.completed_at,
    status: obj.status || obj.data?.status || obj.attributes?.status,
    priority: obj.priority || obj.data?.priority || obj.attributes?.priority,
    due: obj.dueAt || obj.data?.due_date || obj.attributes?.due_date || obj.attributes?.dueDate,
    dueAt: obj.dueAt || obj.data?.due_date || obj.attributes?.due_date || obj.attributes?.dueDate,
    content: obj.body || obj.data?.body || obj.data?.content || obj.attributes?.content || obj.attributes?.body, // API returns 'body' for notes
    body: obj.body || obj.data?.body || obj.data?.content || obj.attributes?.content || obj.attributes?.body, // For notes - both body and content
    created_at: obj.createdAt || obj.created_at,
    updated_at: obj.updatedAt || obj.updated_at,
    conversation_ids: resolvedConversationIds,
    [CONVERSATION_SCOPE_IDS_KEY]: resolvedConversationIds,
    module_ids: obj.data?.moduleIds || obj.attributes?.moduleIds || [],
    _etag: obj._etag ?? etag ?? (obj.version ? String(obj.version) : undefined),
    version: obj.version,
    // Calendar sync fields lifted from attributes to top level. Canonical
    // provider* (#4000); legacy google* names are the fallback for older rows.
    providerCalendarId: obj.providerCalendarId || obj.attributes?.providerCalendarId || obj.googleCalendarId || obj.attributes?.googleCalendarId || obj.data?.googleCalendarId,
    providerCalendarName: obj.providerCalendarName || obj.attributes?.providerCalendarName || obj.googleCalendarName || obj.attributes?.googleCalendarName || obj.data?.googleCalendarName,
    providerEventId: obj.providerEventId || obj.attributes?.providerEventId || obj.googleEventId || obj.attributes?.googleEventId || obj.data?.googleEventId,
    // Outbound-push sync state (#3951). The backend stamps providerSyncState
    // ('synced'|'error') + providerSyncError on the event's attributes; lift
    // them to the top level so the calendar UI can render a "not synced" badge +
    // retry. Canonical provider* first, legacy google* as the fallback for older
    // rows (same legacy→canonical shim as the calendar id/name/eventId above).
    providerSyncState: obj.providerSyncState || obj.attributes?.providerSyncState || obj.googleSyncState || obj.attributes?.googleSyncState,
    providerSyncError: obj.providerSyncError || obj.attributes?.providerSyncError || obj.googleSyncError || obj.attributes?.googleSyncError,
  }

  // Handle asset-specific fields (extract from attributes to top level)
  if (obj.type === 'asset' || obj.object_type === 'asset') {
    const attrs = obj.attributes || {}
    baseTransform.size = obj.size || attrs.fileSize || attrs.size || 0
    baseTransform.mimeType = obj.mimeType || attrs.mimeType || ''
    baseTransform.filename = obj.filename || attrs.filename || obj.title || ''
    baseTransform.storageUrl = obj.storageUrl || attrs.storageUrl || attrs.url || ''
    baseTransform.thumbnailUrl = obj.thumbnailUrl || attrs.thumbnailUrl || ''
  }

  // Handle contact/person specific fields
  if (obj.type === 'contact' || obj.object_type === 'contact') {
    // Parse full name into first_name and last_name if needed
    const displayName = baseTransform.name || '' // Use the normalized name from baseTransform
    const nameParts = displayName.trim().split(' ')

    // Helper to convert empty strings to undefined (for cleaner UI handling)
    const emptyToUndefined = (val: string | null | undefined) => val === '' ? undefined : val

    // Use nullish coalescing (??) to preserve explicit null values (for clearing fields)
    // Convert empty strings to undefined for easier UI handling
    const org = obj.organization ?? obj.attributes?.organization ?? obj.data?.organization
    const sum = obj.summary ?? obj.attributes?.summary ?? obj.data?.summary
    const xh = obj.xHandle ?? obj.attributes?.xHandle ?? obj.data?.xHandle

    // Normalize emails/phones into the rich `*Entries` single-source-of-truth
    // (value + per-element provenance), then DERIVE the plain string arrays from
    // them. The array field is canonical `emails`/`phones` (`{ value, primary,
    // sources }` objects from multi-provider sync, or plain strings for native).
    // The second arg is a singular legacy value kept ONLY to fold a cached
    // pre-backfill response until FE caches expire (canonical rows have none).
    // Rendering an object child directly is what crashed the grid; the unified
    // entries fix that AND give the provenance display one field. buildContactElements (§3.5).
    const emailEntries = buildContactElements(
      obj.emails ?? obj.attributes?.emails ?? obj.data?.emails,
      obj.email ?? obj.attributes?.email ?? obj.data?.email,
    )
    const phoneEntries = buildContactElements(
      obj.phones ?? obj.attributes?.phones ?? obj.data?.phones,
      obj.phone ?? obj.attributes?.phone ?? obj.data?.phone,
    )

    // Organization is canonical; obj.company is the transitional top-level OUTPUT
    // alias (still emitted below for downstream readers not yet on `organization`).
    const extractedCompany = emptyToUndefined(org) ?? emptyToUndefined(obj.company)

    return {
      ...baseTransform,
      // Ensure name is set (baseTransform already has it, but be explicit)
      name: baseTransform.name,
      // Per-field scalar conflict map (§3.6). `fieldProvenance` lives only in the
      // `attributes` JSON the sync writer stamps (contact-sync.ts) — there is no
      // backfill lifting it to a canonical top-level column yet — so this is the
      // chokepoint that lifts it to a typed top-level field for the read-side
      // conflict indicator (canonical top-level-first order, matching every other
      // field). Downstream readers trust the lifted top-level value and must NOT
      // re-read `attributes.fieldProvenance`. Undefined for native/legacy contacts
      // that carry no fieldProvenance (no UI).
      fieldProvenance: (obj.fieldProvenance ?? obj.attributes?.fieldProvenance) as Record<string, FieldConflict> | undefined,
      first_name: obj.firstName ?? obj.attributes?.firstName ?? obj.data?.firstName ?? nameParts[0] ?? '',
      last_name: obj.lastName ?? obj.attributes?.lastName ?? obj.data?.lastName ?? nameParts.slice(1).join(' ') ?? '',
      // Rich entries are the source of truth; the string arrays are derived.
      emailEntries,
      phoneEntries,
      emails: emailEntries.map((e) => e.value),
      phones: phoneEntries.map((e) => e.value),
      organization: emptyToUndefined(org),
      summary: emptyToUndefined(sum),
      email: emailEntries[0]?.value,
      phone: phoneEntries[0]?.value,
      company: extractedCompany,
      xHandle: emptyToUndefined(xh),
      x_handle: emptyToUndefined(xh),
      dealValue: obj.dealValue ?? obj.attributes?.dealValue ?? obj.data?.dealValue,
      probability: obj.probability ?? obj.attributes?.probability ?? obj.data?.probability,
      nextStep: obj.nextStep ?? obj.attributes?.nextStep ?? obj.data?.nextStep,
      notes: obj.notes ?? obj.attributes?.notes ?? obj.data?.notes ?? [],
      // Include location fields from Shopify imports
      city: obj.city ?? obj.attributes?.city ?? obj.data?.city,
      state: obj.state ?? obj.attributes?.state ?? obj.data?.state,
      country: obj.country ?? obj.attributes?.country ?? obj.data?.country,
      address: obj.address ?? obj.attributes?.address ?? obj.data?.address,
      zip: obj.zip ?? obj.attributes?.zip ?? obj.data?.zip,
      // Shopify-specific fields
      shopifyOrders: obj.shopifyOrders ?? obj.attributes?.shopifyOrders ?? obj.data?.shopifyOrders,
      shopifyTotalSpent: obj.shopifyTotalSpent ?? obj.attributes?.shopifyTotalSpent ?? obj.data?.shopifyTotalSpent,
      shopifyTotalGross: obj.shopifyTotalGross ?? obj.attributes?.shopifyTotalGross ?? obj.data?.shopifyTotalGross,
      shopifyTotalRefunded: obj.shopifyTotalRefunded ?? obj.attributes?.shopifyTotalRefunded ?? obj.data?.shopifyTotalRefunded,
      shopifyOrderCount: obj.shopifyOrderCount ?? obj.attributes?.shopifyOrderCount ?? obj.data?.shopifyOrderCount,
      shopifyFirstOrderDate: obj.shopifyFirstOrderDate ?? obj.attributes?.shopifyFirstOrderDate ?? obj.data?.shopifyFirstOrderDate,
      shopifyLastOrderDate: obj.shopifyLastOrderDate ?? obj.attributes?.shopifyLastOrderDate ?? obj.data?.shopifyLastOrderDate,
      shopifyStores: obj.shopifyStores ?? obj.attributes?.shopifyStores ?? obj.data?.shopifyStores,
    }
  }

  // Handle place-specific fields - flatten attributes to top level for easy access
  if (obj.type === 'place' || obj.object_type === 'place') {
    return {
      ...baseTransform,
      // Extract all Google Places enrichment data from attributes to top level
      address: obj.address ?? obj.attributes?.address ?? obj.data?.address,
      geo: obj.geo ?? obj.attributes?.geo ?? obj.data?.geo,
      googlePlaceId: obj.googlePlaceId ?? obj.attributes?.googlePlaceId ?? obj.data?.googlePlaceId,
      rating: obj.rating ?? obj.attributes?.rating ?? obj.data?.rating,
      reviewCount: obj.reviewCount ?? obj.attributes?.reviewCount ?? obj.data?.reviewCount,
      status: obj.status ?? obj.attributes?.status ?? obj.data?.status,
      priceLevel: obj.priceLevel ?? obj.attributes?.priceLevel ?? obj.data?.priceLevel,
      phoneNumber: obj.phoneNumber ?? obj.attributes?.phoneNumber ?? obj.data?.phoneNumber,
      websiteUri: obj.websiteUri ?? obj.attributes?.websiteUri ?? obj.data?.websiteUri,
      googleMapsUri: obj.googleMapsUri ?? obj.attributes?.googleMapsUri ?? obj.data?.googleMapsUri,
      description: obj.description ?? obj.attributes?.description ?? obj.data?.description,
      imageUrl: obj.imageUrl ?? obj.attributes?.imageUrl ?? obj.data?.imageUrl,
      images: obj.images ?? obj.attributes?.images ?? obj.data?.images,
      sections: obj.sections ?? obj.attributes?.sections ?? obj.data?.sections,
      userNotes: obj.userNotes ?? obj.attributes?.userNotes ?? obj.data?.userNotes,
    }
  }

  return baseTransform
}
